"use client";

import React from "react";
import { Waveform } from "@/components/ui/tls/waveform";
import { TPlay } from "@/components/ui/tls/t-play";

export interface WaveformBandProps {
  peaks: number[];
  /** Playback position as a percentage (0-100); only shown while playing. */
  progress: number;
  isPlaying: boolean;
  isLoading: boolean;
  /** Post-mount flag from the parent; gates the waveform vs SSR placeholder. */
  mounted: boolean;
  /** Sound title, used for the play/pause/loading aria-labels. */
  title: string;
  onPlayPause: (e?: React.MouseEvent) => void;
  /** An active preview promotes its row into the full-width signal plate. */
  expanded?: boolean;
}

/**
 * Pure presentation: the card's full-bleed waveform band with its centered
 * play/pause control. All state lives in the parent.
 */
export function WaveformBand({
  peaks,
  progress,
  isPlaying,
  isLoading,
  mounted,
  title,
  onPlayPause,
  expanded = false,
}: WaveformBandProps) {
  return (
    /* The TPlay is the one accessible transport. The surrounding signal is
       deliberately non-interactive, avoiding a nested duplicate control. */
    <div className={expanded ? "sc-band is-expanded" : "sc-band"}>
      {mounted ? (
        <Waveform
          peaks={peaks}
          progress={isPlaying ? progress : 0}
          playColor="rgb(var(--sc-cat))"
          height={44}
        />
      ) : (
        // SSR placeholder
        <div style={{ height: 44, width: "100%", background: "var(--tls-border-subtle)", borderRadius: 4 }} />
      )}

      <TPlay
        playing={isPlaying || isLoading}
        loading={isLoading}
        // The ring at rest is right HERE and is not the TES-2136 drift: a row
        // in a list of 200 genuinely has not resolved its audio until pressed.
        loaded={false}
        ground={expanded ? "inverted" : "canvas"}
        title={title}
        size="css"
        className="sc-play"
        onClick={e => {
          e.stopPropagation();
          onPlayPause();
        }}
      />
    </div>
  );
}
